import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function Stats() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ weight_kg: "", height_cm: "", top_speed_kmh: "", endurance_vo2: "", muscle_index: "", explosive_power: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/stats").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null) payload[k] = k === "note" ? v : parseFloat(v);
      });
      await api.post("/stats", payload);
      toast.success("Saved");
      setForm({ weight_kg: "", height_cm: "", top_speed_kmh: "", endurance_vo2: "", muscle_index: "", explosive_power: "", note: "" });
      await load();
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const chartData = [...items].reverse().map((r, i) => ({
    idx: i + 1,
    weight: r.weight_kg,
    speed: r.top_speed_kmh,
    vo2: r.endurance_vo2,
  }));

  const fields = [
    ["weight_kg", t.stats.weight],
    ["height_cm", t.stats.height],
    ["top_speed_kmh", t.stats.speed],
    ["endurance_vo2", t.stats.vo2],
    ["muscle_index", t.stats.muscle],
    ["explosive_power", t.stats.power],
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="stats-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">TELEMETRY</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="stats-title">{t.stats.title}</h1>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="rounded-xl border border-[#1E2638] bg-[#121721] p-6 space-y-3">
          <div className="font-display font-bold uppercase text-lg">{t.stats.add}</div>
          {fields.map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{label}</Label>
              <Input
                type="number" step="0.1"
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`stats-input-${k}`}
                className="mt-1 bg-[#090C10] border-[#1E2638] text-white"
              />
            </div>
          ))}
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.stats.note}</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="stats-input-note" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <Button type="submit" disabled={saving} data-testid="stats-save-btn" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
            {saving ? "…" : t.stats.save}
          </Button>
        </form>

        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
            <div className="font-display font-bold uppercase text-lg mb-3">Evolution</div>
            {chartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-slate-500 text-sm">Log your first record →</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#1E2638" strokeDasharray="3 3" />
                  <XAxis dataKey="idx" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#090C10", border: "1px solid #1E2638", borderRadius: 8 }} />
                  <Line type="monotone" dataKey="weight" stroke="#00F076" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="speed" stroke="#FFB800" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="vo2" stroke="#FF2E54" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
            <div className="font-display font-bold uppercase text-lg mb-3">{t.stats.history}</div>
            <div className="space-y-2 max-h-80 overflow-auto">
              {items.length === 0 && <div className="text-slate-500 text-sm">—</div>}
              {items.map((r) => (
                <div key={r.id} className="flex flex-wrap gap-3 text-xs font-mono-tech border-b border-[#1E2638] py-2" data-testid={`stats-row-${r.id}`}>
                  <span className="text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span>
                  {r.weight_kg && <span>W {r.weight_kg}kg</span>}
                  {r.top_speed_kmh && <span className="text-[#00F076]">SPD {r.top_speed_kmh}</span>}
                  {r.endurance_vo2 && <span className="text-amber-400">VO2 {r.endurance_vo2}</span>}
                  {r.muscle_index && <span>MI {r.muscle_index}</span>}
                  {r.explosive_power && <span>PWR {r.explosive_power}</span>}
                  {r.note && <span className="text-slate-400">"{r.note}"</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
